import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

/**
 * Klaret Avatar (handoff §10.10). No RAC primitive — plain styled element.
 * Sizes 24 / 32 / 40 px (xs/sm/md) + lg/xl extras matching existing API.
 * Fallback: two-letter initials, caption/footnote 600, deterministic
 * chart-palette background derived from the name string.
 * Stack ring: 2px --tvl-raised. Overflow "+N" = sunken avatar.
 * Square r1 variant for module/company marks.
 */

// Deterministic chart-palette backgrounds (§2.5 light values; dark values via token override)
// We pick from the 8 categorical series using charCode sum mod 8.
const CHART_PALETTE_LIGHT = [
  '#932E3A', // series 1 claret
  '#1A7078', // series 2 petrol
  '#A5762E', // series 3 ochre
  '#537746', // series 4 moss
  '#754E71', // series 5 mauve
  '#465574', // series 6 slate
  '#A46041', // series 7 copper
  '#574949', // series 8 graphite
] as const;

// On-color text — use --tvl-on-accent (paper) for all, as these are all dark enough
const CHART_TEXT = 'var(--tvl-on-accent)';

function pickPalette(name: string): string {
  if (!name) return CHART_PALETTE_LIGHT[0];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return CHART_PALETTE_LIGHT[sum % CHART_PALETTE_LIGHT.length];
}

function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

const avatarVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center overflow-hidden font-semibold select-none',
  {
    variants: {
      size: {
        xs: 'h-6 w-6 text-[10px]',   // 24px
        sm: 'h-8 w-8 text-xs',        // 32px
        md: 'h-10 w-10 text-sm',      // 40px
        lg: 'h-11 w-11 text-base',    // 44px
        xl: 'h-14 w-14 text-lg',      // 56px
      },
      shape: {
        round: 'rounded-[var(--tvl-r-full)]',
        square: 'rounded-[var(--tvl-r1)]', // r1 for module/company marks
      },
    },
    defaultVariants: { size: 'md', shape: 'round' },
  },
);

const presenceColor: Record<string, string> = {
  online: 'bg-[var(--tvl-success)]',
  offline: 'bg-[var(--tvl-ink-3)]',
  busy: 'bg-[var(--tvl-danger)]',
  away: 'bg-[var(--tvl-warning)]',
};

const presenceSize: Record<string, string> = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-3.5 w-3.5',
};

export interface AvatarProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  name?: string;
  alt?: string;
  presence?: 'online' | 'offline' | 'busy' | 'away';
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, size = 'md', shape = 'round', src, name, alt, presence, ...props }, ref) => {
    const [imgError, setImgError] = React.useState(false);
    const showImage = src && !imgError;
    const initials = getInitials(name);
    const bgColor = pickPalette(name ?? '');

    return (
      <span className="relative inline-flex">
        <span
          ref={ref}
          className={cn(avatarVariants({ size, shape }), className)}
          style={!showImage ? { backgroundColor: bgColor, color: CHART_TEXT } : undefined}
          aria-label={alt ?? name}
          role="img"
          {...props}
        >
          {showImage ? (
            <img
              src={src}
              alt={alt ?? name ?? ''}
              className="aspect-square h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <span aria-hidden className="leading-none">
              {initials}
            </span>
          )}
        </span>

        {/* presence dot */}
        {presence ? (
          <span
            aria-label={`presence: ${presence}`}
            className={cn(
              'absolute bottom-0 right-0 rounded-[var(--tvl-r-full)]',
              'ring-2 ring-[var(--tvl-raised)]',
              presenceSize[size ?? 'md'],
              presenceColor[presence],
            )}
          />
        ) : null}
      </span>
    );
  },
);
Avatar.displayName = 'Avatar';
